import { gql, useLazyQuery } from "@apollo/client";
import { useState } from "react";
import { Alert } from "components/base/Toast";
import Button from "components/base/Button";
import { Text } from "components/base/Form";
import Page from "components/base/Page";
import { useNavigate } from "react-router-dom";
import { FiDownload } from "react-icons/fi";
import { download } from "utils/http";

const CUSTOM_FORM_PREVIEW = gql`
  query CUSTOM_FORM_PREVIEW($shipmentId: ID!, $rowIds: [ID]!, $notes: String) {
    customFormPreview(shipmentId: $shipmentId, rowIds: $rowIds, notes: $notes)
  }
`;

const CustomFormGenerator = ({ shipment }) => {
  const navigate = useNavigate();
  const [selectedRowIds, setSelectedRowIds] = useState(
    shipment.shipmentInvoices
      .flatMap((i) => i.shipmentInvoiceRows)
      .map((i) => i.id),
  );
  const [notesForCustomform, setNotesForCustomform] = useState("");
  const [generating, setGenerating] = useState("");
  const [loadPreview, { loading: previewLoading }] =
    useLazyQuery(CUSTOM_FORM_PREVIEW);

  const selectedRows = shipment.shipmentInvoices
    .flatMap((i) => i.shipmentInvoiceRows)
    .filter((i) => selectedRowIds.includes(i.id));
  const totalQty = selectedRows.reduce((acc, row) => acc + row.qty, 0);
  const totalCarton = selectedRows.reduce(
    (acc, row) => acc + row.totalCarton,
    0,
  );
  const totalNetWeight = selectedRows
    .reduce((acc, row) => acc + row.totalNetWeight, 0)
    .toFixed(2);
  const totalGrossWeight = selectedRows
    .reduce((acc, row) => acc + row.totalGrossWeight, 0)
    .toFixed(2);
  const totalCbm = selectedRows
    .reduce((acc, row) => acc + row.totalCbm, 0)
    .toFixed(3);
  const totalValue = selectedRows
    .reduce((acc, row) => acc + row.customsValue * row.qty, 0)
    .toFixed(2);

  const requestData = {
    row_ids: selectedRowIds,
    notes: notesForCustomform,
  };

  async function showPreview() {
    if (!selectedRows.length) {
      Alert("error", "Please select at least one product.");
      return;
    }
    try {
      const { data } = await loadPreview({
        variables: {
          shipmentId: shipment.id,
          rowIds: selectedRowIds,
          notes: notesForCustomform,
        },
      });
      navigate(`/shipment/doc/custom-form/${shipment.id}/preview`, {
        state: { shipment, requestData, html: data.customFormPreview },
      });
    } catch (error) {
      Alert("error", error.message);
    }
  }

  function generate(fileType) {
    if (!selectedRows.length) {
      Alert("error", "Please select at least one product.");
      return;
    }
    setGenerating(fileType);
    const fallbackName = `Customform-SO${shipment.invoiceNumber.replace("/", "-")}`;
    download(
      `/shipment/${shipment.id}/custom-form/${fileType}/`,
      `${fallbackName}.${fileType}`,
      requestData,
      { useResponseFilename: true },
    )
      .catch((error) => Alert("error", String(error)))
      .finally(() => setGenerating(""));
  }

  return (
    <Page
      className="lg:h-screen"
      title="Edit Custom Form"
      backTo={`/shipment/shipments/${shipment.id}`}
      rightButtons={
        <div className="flex gap-6">
          <Button
            icon={<FiDownload />}
            loading={generating === "pdf"}
            disabled={previewLoading || Boolean(generating)}
            onClick={() => generate("pdf")}
          >
            Generate PDF
          </Button>
          <Button
            icon={<FiDownload />}
            loading={generating === "xlsx"}
            disabled={previewLoading || Boolean(generating)}
            onClick={() => generate("xlsx")}
          >
            Generate Excel
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 mx-auto my-8 px-6">
        <div className="space-y-6 text-sm card">
          <table>
            <thead>
              <tr className="whitespace-nowrap">
                <th>
                  <div className="flex items-center justify-center">
                    <input
                      className="cursor-pointer"
                      type="checkbox"
                      checked={
                        selectedRowIds.length ===
                        shipment.shipmentInvoices.flatMap(
                          (i) => i.shipmentInvoiceRows,
                        ).length
                      }
                      onChange={() =>
                        setSelectedRowIds((prev) =>
                          prev.length ===
                          shipment.shipmentInvoices.flatMap(
                            (i) => i.shipmentInvoiceRows,
                          ).length
                            ? []
                            : shipment.shipmentInvoices
                                .flatMap((i) => i.shipmentInvoiceRows)
                                .map((i) => i.id),
                        )
                      }
                    />
                  </div>
                </th>
                <th>货名</th>
                <th>型号</th>
                <th>牌子</th>
                <th>规格</th>
                <th>用途</th>
                <th>材质</th>
                <th>HS Code</th>
                <th className="text-center">产品图片</th>
                <th className="text-right">数量</th>
                <th className="text-right">包装件数</th>
                <th className="text-right">包装类型</th>
                <th className="text-right">净重(KGS)</th>
                <th className="text-right">毛重</th>
                <th className="text-right">体积(CBM)</th>
                <th className="text-right">货值(¥)</th>
              </tr>
            </thead>
            <tbody>
              {shipment.shipmentInvoices.map((shipmentInvoice) =>
                shipmentInvoice.shipmentInvoiceRows.map((row) => (
                  <tr
                    key={row.id}
                    className={`space-x-4 cursor-pointer hover:text-blue-600 border-y
                  ${selectedRowIds.includes(row.id) ? "" : "opacity-50"}
                  `}
                    onClick={() =>
                      setSelectedRowIds((prev) =>
                        selectedRowIds.includes(row.id)
                          ? prev.filter((i) => i !== row.id)
                          : [...prev, row.id],
                      )
                    }
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRowIds.includes(row.id)}
                        onChange={() =>
                          setSelectedRowIds((prev) =>
                            selectedRowIds.includes(row.id)
                              ? prev.filter((i) => i !== row.id)
                              : [...prev, row.id],
                          )
                        }
                      />
                    </td>
                    <td>
                      <div>
                        <div>{row.packingName}</div>
                        <div>{row.customsName}</div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div>{row.itemNumber}</div>
                      </div>
                    </td>
                    <td>{row.customsBrand}</td>
                    <td>{row.customsSize}</td>
                    <td>{row.customsUsage}</td>
                    <td>
                      {row.customsMaterials
                        .split(",")
                        .map((material) => material.trim())
                        .join(", ")}
                    </td>
                    <td>{row.hsCode}</td>
                    <td>
                      <div className="flex items-center flex-wrap justify-center">
                        {row.product?.images?.length > 0 ? (
                          row.product.images.map((image) => (
                            <img
                              key={image.id}
                              src={image.url}
                              alt={row.productName}
                              className="w-10 h-10 object-contain rounded-md"
                            />
                          ))
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-md" />
                        )}
                      </div>
                    </td>
                    <td className="text-right">{row.qty}</td>
                    <td className="text-right">{row.totalCarton}</td>
                    <td className="text-right">纸箱</td>
                    <td className="text-right">{row.totalNetWeight}</td>
                    <td className="text-right">{row.totalGrossWeight}</td>
                    <td className="text-right">{row.totalCbm}</td>
                    <td className="text-right">{row.customsValue}</td>
                  </tr>
                )),
              )}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td colSpan="9">TOTAL</td>
                <td className="text-right">{totalQty}</td>
                <td className="text-right">{totalCarton}</td>
                <td></td>
                <td className="text-right">{totalNetWeight}</td>
                <td className="text-right">{totalGrossWeight}</td>
                <td className="text-right">{totalCbm}</td>
                <td className="text-right">{totalValue}</td>
              </tr>
            </tfoot>
          </table>

          <hr />

          <div className="">
            <h5>Notes</h5>
            <Text
              className="mt-4"
              value={notesForCustomform}
              onChange={(e) => setNotesForCustomform(e.target.value)}
            />
          </div>
        </div>

        <Button
          primary
          className="self-start text-sm !px-4 !py-1.5"
          disabled={Boolean(generating)}
          loading={previewLoading}
          onClick={showPreview}
        >
          Preview
        </Button>
      </div>
    </Page>
  );
};

export default CustomFormGenerator;
